import { useEffect } from 'react'
import { useFormik } from 'formik'

import { validateRequestForm } from 'components/inbox/RequestForm/utils'
import { logError } from 'utils/monitoring/datadog'

export const useMemberCommsFormik = ({
  ashSessionId = '',
  suggestedMember,
  suggestedExpense,
  selectedSpecialty,
  postMessage,
  members,
  searchResults,
  expenses,
  fields = [],
  title,
  requestTypeId,
  featureFlags,
}) => {
  // Use store for default values
  const formik = useFormik({
    initialValues: {
      subject: '', // title of request if type === other
      drug: '', // drug name, dosage and quantity
      service: '', // provider PLA service
      serviceDate: '', // provider PLA service date
      provider: '', // provider PLA provider name & address
      message: '', // main message of request
      selectedMember: suggestedMember?.value || '',
      selectedExpense: suggestedExpense?.uuid || '',
      attachments: [],
      // improve search results fields
      issueType: '',
      practiceName: '',
      address: '',
      providerSpecialty: '',
      unit: '',
      city: '',
      state: '',
      zip: '',
      phone: '',
      website: '',
    },
    validate: values =>
      validateRequestForm(values, title, featureFlags, expenses.length),
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: values => {
      try {
        // Use the selected values for member and expense to get their details
        // because we need to send the full objects, not just the UUIDs.
        const memberDetails = members.find(
          member => member.uuid === values.selectedMember,
        )
        // the "searchResults" are basically just expenses :shrug:
        const expenseDetails = searchResults.find(
          expense => expense.uuid === values.selectedExpense,
        )

        // If the productType is FIXED_INDEMNITY, Jira is expecting EB
        const productType =
          memberDetails?.coverageType[0] === 'FIXED_INDEMNITY'
            ? 'EB'
            : memberDetails?.coverageType[0]

        // Set the request type params req for the Jira create ticket endpoint
        const requestParams = {
          // Spread, because `postMessage` appends every key it is handed and the
          // service only writes the Jira field when non-blank — a key that is
          // always present would lose "not from Ash" vs "from Ash, id missing".
          ...(ashSessionId && { ashSessionId }),
          files: values?.attachments || '',
          customerTitle: values?.subject || title,
          drug: values?.drug || '',
          expenseId: expenseDetails?.id || '',
          expenseUuid: expenseDetails?.uuid || '',
          memberGroup: memberDetails?.asoGroupDto?.name || '',
          memberUuid: memberDetails?.uuid || '',
          message: values?.message || '',
          requestType: 'Member Care Issue Tracking',
          requestTypeId: requestTypeId || '',
          // the priority needs to be a string not an int for the Jira API
          priority: '3',
          productType: productType || '',
          project: 'Member',
          provider: values?.provider || '',
          address: values?.address || '',
          // The specialty field is from state, this is a hidden form field.
          // When the FF is on, the specialty will be passed in the request.
          // If the Contentful & Jira prechecked/specialty lists become out of
          // sync, the requests will error. In this case, the FF can be turned
          // off while the lists are updated.
          specialty:
            featureFlags['lightsOn-memberComm-prefillSpecialty'] &&
            selectedSpecialty !== ''
              ? `Pre-checked providers - ${selectedSpecialty}`
              : '',
          service: values?.service || '',
          serviceDate: values?.serviceDate || '',
          subject: values?.subject || title || '',
          // improve search results fields
          issueType: values?.issueType || '',
          practiceName: values?.practiceName || '',
          providerSpecialty: values?.providerSpecialty || '',
          unit: values?.unit || '',
          city: values?.city || '',
          state: values?.state || '',
          zip: values?.zip || '',
          phone: values?.phone || '',
          website: values?.website || '',
        }
        postMessage(requestParams)
      } catch (error) {
        logError('Error submitting message:', { error })
      }
    },
  })

  const { validateForm } = formik

  useEffect(() => {
    validateForm()
    // this is weird that we have these in the dep array, but this is for
    // auto-filling the suggestedMember and suggestedExpense drop downs.
    // when these change we need to revalidate the pre-selected inputs
    // in the drop down. Sometimes these won't have values.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [members, expenses])

  // Sync suggestedMember only once on mount or when it first resolves
  useEffect(() => {
    if (suggestedMember?.value && !formik.values.selectedMember) {
      formik.setFieldValue('selectedMember', suggestedMember.value)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggestedMember?.value])

  // Sync suggestedExpense only once on mount or when it first resolves
  useEffect(() => {
    if (!fields.includes('expense')) return

    if (suggestedExpense?.uuid && !formik.values.selectedExpense) {
      formik.setFieldValue('selectedExpense', suggestedExpense.uuid)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggestedExpense?.uuid])

  return formik
}
